/**
 * Workflow Steps Configuration
 *
 * Hierarchical structure matching app sections.
 * Categories represent main app areas (Signatures, Users, etc.)
 * Items within categories represent specific pages/sections
 * Actions within items represent specific operations that can be performed
 */

export const WORKFLOW_STEP_CATEGORIES = [
  {
    label: 'Signatures',
    value: 'signatures',
    description: 'Email signature management',
    items: [
      {
        label: 'Designer Studio',
        value: 'signatures_studio',
        description: 'Create and edit email signatures',
        actions: [
          {
            label: 'Create Signature',
            value: 'signature_create',
            description: 'Create a new email signature',
            searchWords: ['signature', 'email', 'create', 'new', 'add', 'banner', 'template']
          },
          {
            label: 'Update Signature',
            value: 'signature_update',
            description: 'Update an existing signature',
            searchWords: ['signature', 'email', 'update', 'edit', 'modify', 'change', 'banner']
          },
          {
            label: 'Delete Signature',
            value: 'signature_delete',
            description: 'Delete a signature',
            searchWords: ['signature', 'email', 'delete', 'remove', 'banner']
          },
          {
            label: 'Clone Signature',
            value: 'signature_clone',
            description: 'Clone an existing signature',
            searchWords: ['signature', 'email', 'clone', 'copy', 'duplicate', 'banner']
          },
          {
            label: 'Clone to Domain',
            value: 'signature_clone_domain',
            description: 'Clone a signature to a domain',
            searchWords: ['signature', 'email', 'clone', 'copy', 'duplicate', 'domain', 'banner', 'to']
          },
          {
            label: 'Archive Signature',
            value: 'signature_archive',
            description: 'Archive a signature',
            searchWords: ['signature', 'email', 'archive', 'hide', 'banner']
          },
          {
            label: 'Unarchive Signature',
            value: 'signature_unarchive',
            description: 'Unarchive a signature',
            searchWords: ['signature', 'email', 'unarchive', 'restore', 'unhide', 'banner']
          }
        ]
      },
      {
        label: 'Routing',
        value: 'signatures_routing',
        description: 'Assign signatures to groups',
        actions: [
          {
            label: 'Create Route',
            value: 'signature_route_create',
            description: 'Create a new signature routing rule',
            searchWords: ['signature', 'route', 'routing', 'assign', 'create', 'add', 'group', 'banner']
          },
          {
            label: 'Update Route',
            value: 'signature_route_update',
            description: 'Update an existing routing rule',
            searchWords: ['signature', 'route', 'routing', 'update', 'edit', 'modify', 'group', 'banner']
          },
          {
            label: 'Delete Route',
            value: 'signature_route_delete',
            description: 'Delete a routing rule',
            searchWords: ['signature', 'route', 'routing', 'delete', 'remove', 'unassign', 'group', 'banner']
          },
          {
            label: 'Change Priority',
            value: 'signature_route_priority',
            description: 'Change routing rule priority',
            searchWords: ['signature', 'route', 'routing', 'priority', 'order', 'reorder', 'move', 'rank']
          }
        ]
      },
      {
        label: 'Pusher',
        value: 'signatures_pusher',
        description: 'Push signatures to users',
        actions: [
          {
            label: 'New Push',
            value: 'signature_push',
            description: 'Push signatures to user mailboxes',
            searchWords: ['signature', 'push', 'pusher', 'deploy', 'send', 'user', 'mailbox', 'banner']
          },
          {
            label: 'Repeat Push',
            value: 'signature_push_repeat',
            description: 'Repeat a signature push to users',
            searchWords: ['signature', 'push', 'pusher', 'repeat', 'resend', 'deploy', 'user', 'mailbox', 'banner']
          }
        ]
      }
    ]
  },
  {
    label: 'Business Cards',
    value: 'bizcards',
    description: 'Business card management',
    items: [
      {
        label: 'Designer Studio',
        value: 'bizcards_studio',
        description: 'Create and edit business cards',
        actions: [
          {
            label: 'Create Business Card',
            value: 'bizcard_create',
            description: 'Create a new business card',
            searchWords: ['bizcard', 'business card', 'vcard', 'create', 'new', 'add', 'signature', 'email']
          },
          {
            label: 'Update Business Card',
            value: 'bizcard_update',
            description: 'Update an existing business card',
            searchWords: ['bizcard', 'business card', 'vcard', 'update', 'edit', 'modify', 'change', 'signature']
          },
          {
            label: 'Delete Business Card',
            value: 'bizcard_delete',
            description: 'Delete a business card',
            searchWords: ['bizcard', 'business card', 'vcard', 'delete', 'remove', 'signature']
          },
          {
            label: 'Clone Business Card',
            value: 'bizcard_clone',
            description: 'Clone an existing business card',
            searchWords: ['bizcard', 'business card', 'vcard', 'clone', 'copy', 'duplicate', 'signature']
          }
        ]
      },
      {
        label: 'Routing',
        value: 'bizcards_routing',
        description: 'Assign business cards to groups',
        actions: [
          {
            label: 'Create Route',
            value: 'bizcard_route_create',
            description: 'Create a new business card routing rule',
            searchWords: [
              'bizcard',
              'business card',
              'vcard',
              'route',
              'routing',
              'assign',
              'create',
              'add',
              'group',
              'signature'
            ]
          },
          {
            label: 'Update Route',
            value: 'bizcard_route_update',
            description: 'Update an existing routing rule',
            searchWords: [
              'bizcard',
              'business card',
              'vcard',
              'route',
              'routing',
              'update',
              'edit',
              'modify',
              'group',
              'signature'
            ]
          },
          {
            label: 'Delete Route',
            value: 'bizcard_route_delete',
            description: 'Delete a routing rule',
            searchWords: [
              'bizcard',
              'business card',
              'vcard',
              'route',
              'routing',
              'delete',
              'remove',
              'unassign',
              'group',
              'signature'
            ]
          },
          {
            label: 'Change Priority',
            value: 'bizcard_route_priority',
            description: 'Change routing rule priority',
            searchWords: [
              'bizcard',
              'business card',
              'vcard',
              'route',
              'routing',
              'priority',
              'order',
              'reorder',
              'move',
              'rank'
            ]
          }
        ]
      }
    ]
  },
  {
    label: 'Marketing',
    value: 'marketing',
    description: 'Marketing campaign management',
    items: [
      {
        label: 'Campaigns',
        value: 'marketing_campaigns',
        description: 'Manage marketing campaigns',
        actions: [
          {
            label: 'Create Campaign',
            value: 'campaign_create',
            description: 'Create a new marketing campaign',
            searchWords: ['campaign', 'marketing', 'ad', 'create', 'new', 'add', 'banner', 'email']
          },
          {
            label: 'Update Campaign',
            value: 'campaign_update',
            description: 'Update an existing campaign',
            searchWords: ['campaign', 'marketing', 'ad', 'update', 'edit', 'modify', 'change']
          },
          {
            label: 'Delete Campaign',
            value: 'campaign_delete',
            description: 'Delete a marketing campaign',
            searchWords: ['campaign', 'marketing', 'ad', 'delete', 'remove']
          },
          {
            label: 'Clone Campaign',
            value: 'campaign_clone',
            description: 'Clone an existing campaign',
            searchWords: ['campaign', 'marketing', 'ad', 'clone', 'copy', 'duplicate']
          }
        ]
      },
      {
        label: 'Targeting',
        value: 'marketing_targeting',
        description: 'Account-based marketing targeting',
        actions: [
          {
            label: 'Create Targeting',
            value: 'targeting_create',
            description: 'Create targeting rules',
            searchWords: ['targeting', 'target', 'account', 'abm', 'marketing', 'create', 'add', 'campaign']
          },
          {
            label: 'Update Targeting',
            value: 'targeting_update',
            description: 'Update targeting rules',
            searchWords: ['targeting', 'target', 'account', 'abm', 'marketing', 'update', 'edit', 'modify', 'campaign']
          },
          {
            label: 'Delete Targeting',
            value: 'targeting_delete',
            description: 'Delete targeting rules',
            searchWords: ['targeting', 'target', 'account', 'abm', 'marketing', 'delete', 'remove', 'campaign']
          }
        ],
        items: [
          {
            label: 'Groups',
            value: 'marketing_targeting_groups',
            description: 'Manage targeting group priority',
            actions: [
              {
                label: 'Change Priority',
                value: 'targeting_group_priority',
                description: 'Change targeting group priority',
                searchWords: ['targeting', 'group', 'priority', 'order', 'reorder', 'move', 'rank', 'abm']
              }
            ]
          }
        ]
      }
    ]
  },
  {
    label: 'Manage',
    value: 'manage',
    description: 'System management',
    items: [
      {
        label: 'Images',
        value: 'manage_images',
        description: 'Manage image assets',
        actions: [
          {
            label: 'Upload Image',
            value: 'image_single_create',
            description: 'Upload a single image',
            searchWords: ['image', 'picture', 'photo', 'upload', 'add', 'create', 'asset', 'media']
          },
          {
            label: 'Bulk Upload Images',
            value: 'image_bulk_create',
            description: 'Upload multiple images at once',
            searchWords: ['image', 'picture', 'photo', 'bulk', 'upload', 'multiple', 'batch', 'asset', 'media']
          },
          {
            label: 'Update Image',
            value: 'image_update',
            description: 'Update an existing image',
            searchWords: ['image', 'picture', 'photo', 'update', 'edit', 'modify', 'replace', 'asset']
          },
          {
            label: 'Delete Image',
            value: 'image_delete',
            description: 'Delete an image',
            searchWords: ['image', 'picture', 'photo', 'delete', 'remove', 'asset', 'media']
          }
        ]
      },
      {
        label: 'Documents',
        value: 'manage_documents',
        description: 'Manage document assets',
        actions: [
          {
            label: 'Create Document',
            value: 'document_create',
            description: 'Upload a new document',
            searchWords: ['document', 'doc', 'pdf', 'file', 'upload', 'create', 'add', 'asset', 'media']
          },
          {
            label: 'Update Document',
            value: 'document_update',
            description: 'Update an existing document',
            searchWords: ['document', 'doc', 'pdf', 'file', 'update', 'edit', 'modify', 'replace', 'asset']
          },
          {
            label: 'Delete Document',
            value: 'document_delete',
            description: 'Delete a document',
            searchWords: ['document', 'doc', 'pdf', 'file', 'delete', 'remove', 'asset', 'media']
          }
        ]
      },
      {
        label: 'Videos',
        value: 'manage_videos',
        description: 'Manage video assets',
        actions: [
          {
            label: 'Create Video',
            value: 'video_create',
            description: 'Upload a new video',
            searchWords: ['video', 'movie', 'clip', 'upload', 'create', 'add', 'asset', 'media']
          },
          {
            label: 'Update Video',
            value: 'video_update',
            description: 'Update an existing video',
            searchWords: ['video', 'movie', 'clip', 'update', 'edit', 'modify', 'replace', ' asset']
          },
          {
            label: 'Delete Video',
            value: 'video_delete',
            description: 'Delete a video',
            searchWords: ['video', 'movie', 'clip', 'delete', 'remove', 'asset', 'media']
          }
        ]
      },
      {
        label: 'Domain Settings',
        value: 'manage_domain_settings',
        description: 'Manage domain settings',
        actions: [
          {
            label: 'Update Domain Settings',
            value: 'domain_settings_update',
            description: 'Update domain configuration settings',
            searchWords: ['domain', 'settings', 'update', 'edit', 'modify', 'configuration']
          }
        ]
      },
      {
        label: 'Groups',
        value: 'manage_groups',
        description: 'Manage user groups',
        actions: [
          {
            label: 'Create Group',
            value: 'group_create',
            description: 'Create a new user group',
            searchWords: ['group', 'team', 'department', 'create', 'new', 'add', 'user', 'routing']
          },
          {
            label: 'Update Group',
            value: 'group_update',
            description: 'Update group details',
            searchWords: ['group', 'team', 'department', 'update', 'edit', 'modify', 'change', 'user']
          },
          {
            label: 'Delete Group',
            value: 'group_delete',
            description: 'Delete a user group',
            searchWords: ['group', 'team', 'department', 'delete', 'remove', 'user']
          },
          {
            label: 'Clone Group',
            value: 'group_clone',
            description: 'Clone an existing user group',
            searchWords: ['group', 'team', 'department', 'clone', 'copy', 'duplicate', 'user']
          }
        ]
      },
      {
        label: 'Integrations',
        value: 'manage_integrations',
        description: 'Manage third-party integrations',
        actions: [
          {
            label: 'Create Integration',
            value: 'integration_create',
            description: 'Create a new integration',
            searchWords: ['integration', 'api', 'webhook', 'connect', 'create', 'new', 'add', 'third party']
          },
          {
            label: 'Update Integration',
            value: 'integration_update',
            description: 'Update integration settings',
            searchWords: ['integration', 'api', 'webhook', 'connect', 'update', 'edit', 'modify', 'settings']
          },
          {
            label: 'Delete Integration',
            value: 'integration_delete',
            description: 'Delete an integration',
            searchWords: ['integration', 'api', 'webhook', 'connect', 'delete', 'remove', 'disconnect']
          }
        ]
      },
      {
        label: 'Users',
        value: 'manage_users',
        description: 'Manage user accounts',
        actions: [
          {
            label: 'Sync User(s)',
            value: 'user_create',
            description: 'Sync user accounts',
            searchWords: ['user', 'sync', 'account', 'create', 'add', 'import', 'azure', 'ad']
          },
          {
            label: 'Update User',
            value: 'user_update',
            description: 'Update user details',
            searchWords: ['user', 'account', 'update', 'edit', 'modify', 'change']
          },
          {
            label: 'Enable User',
            value: 'user_enable',
            description: 'Enable a user account',
            searchWords: ['user', 'account', 'enable', 'activate', 'turn on']
          },
          {
            label: 'Disable User',
            value: 'user_disable',
            description: 'Disable a user account',
            searchWords: ['user', 'account', 'disable', 'deactivate', 'turn off']
          },
          {
            label: 'Send Reminder',
            value: 'user_reminder',
            description: 'Send a reminder to a user',
            searchWords: ['user', 'reminder', 'notification', 'send', 'email', 'alert']
          },
          {
            label: 'Remove Admin Roles',
            value: 'user_remove_admin_roles',
            description: 'Remove admin roles from one or more users',
            searchWords: [
              'user',
              'admin',
              'roles',
              'remove',
              'strip',
              'revoke',
              'billing',
              'domainadmin',
              'marketing',
              'signatures'
            ]
          }
        ]
      }
    ]
  },
  {
    label: 'Staff',
    value: 'staff',
    description: 'Staff administration tools',
    items: [
      {
        label: 'Manage Domains',
        value: 'staff_manage_domains',
        description: 'Domain management operations',
        actions: [
          {
            label: 'Create Domain',
            value: 'domain_create',
            description: 'Create a new domain',
            searchWords: ['domain', 'create', 'new', 'add', 'provision', 'setup', 'staff']
          },
          {
            label: 'Clone Domain',
            value: 'domain_clone',
            description: 'Clone a domain configuration to other domains',
            searchWords: ['domain', 'clone', 'copy', 'duplicate', 'staff']
          },
          {
            label: 'Manage MDM Accounts',
            value: 'domain_mdm',
            description: 'Manage MDM accounts for a domain',
            searchWords: ['domain', 'mdm', 'account', 'manage', 'mobile', 'device', 'staff']
          },
          {
            label: 'Update Billing ID',
            value: 'domain_billing_update',
            description: 'Update the Maxio and Younium billing IDs for a domain',
            searchWords: ['domain', 'billing', 'billing id', 'update', 'maxio', 'younium', 'staff']
          }
        ]
      }
    ]
  }
];

/**
 * Get all workflow actions (flattened)
 */
export const getAllWorkflowActions = () => {
  return WORKFLOW_STEP_CATEGORIES.flatMap((category) =>
    category.items.flatMap((item) => {
      const results = [];

      // Process direct actions if they exist
      if (item.actions) {
        results.push(
          ...item.actions.map((action) => ({
            ...action,
            category: category.value,
            categoryLabel: category.label,
            item: item.value,
            itemLabel: item.label
          }))
        );
      }

      // Process sub-items if they exist
      if (item.items) {
        results.push(
          ...item.items.flatMap((subItem) =>
            subItem.actions.map((action) => ({
              ...action,
              category: category.value,
              categoryLabel: category.label,
              item: item.value,
              itemLabel: item.label,
              subItem: subItem.value,
              subItemLabel: subItem.label
            }))
          )
        );
      }

      return results;
    })
  );
};

/**
 * Get a workflow action by its value
 */
export const getWorkflowActionByValue = (value) => {
  for (const category of WORKFLOW_STEP_CATEGORIES) {
    for (const item of category.items) {
      // Check direct actions first
      if (item.actions) {
        const action = item.actions.find((a) => a.value === value);
        if (action) {
          return {
            ...action,
            category: category.value,
            categoryLabel: category.label,
            item: item.value,
            itemLabel: item.label
          };
        }
      }

      // Check sub-items if they exist
      if (item.items) {
        for (const subItem of item.items) {
          const action = subItem.actions.find((a) => a.value === value);
          if (action) {
            return {
              ...action,
              category: category.value,
              categoryLabel: category.label,
              item: item.value,
              itemLabel: item.label,
              subItem: subItem.value,
              subItemLabel: subItem.label
            };
          }
        }
      }
    }
  }
  return null;
};

/**
 * Get a category by its value
 */
export const getCategoryByValue = (value) => {
  return WORKFLOW_STEP_CATEGORIES.find((category) => category.value === value);
};

/**
 * Get an item by its value
 */
export const getItemByValue = (value) => {
  for (const category of WORKFLOW_STEP_CATEGORIES) {
    const item = category.items.find((i) => i.value === value);
    if (item) {
      return {
        ...item,
        category: category.value,
        categoryLabel: category.label
      };
    }
  }
  return null;
};
